import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FortuneSign } from "@/data/fortuneData";
import { Download, Share2 } from "lucide-react";

interface FortuneCardProps {
  sign: FortuneSign;
  onSave?: () => void;
  onShare?: () => void;
}

export const FortuneCard = ({ sign, onSave, onShare }: FortuneCardProps) => {
  const getLevelColor = (level: string) => {
    switch (level) {
      case '上上':
        return 'bg-red-600 text-white';
      case '上中':
      case '上下':
        return 'bg-red-500 text-white';
      case '中上':
      case '中中':
      case '中下':
        return 'bg-yellow-500 text-white';
      case '下上':
      case '下中':
      case '下下':
        return 'bg-gray-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `黃大仙解簽 - ${sign.title}`,
        text: `${sign.poem}\n\n${sign.meaning}`,
        url: window.location.href
      });
    } else {
      // 備用分享方式
      const text = `黃大仙解簽 - ${sign.title}\n\n${sign.poem}\n\n${sign.meaning}`;
      navigator.clipboard.writeText(text);
      alert('簽文已複製到剪貼板');
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto bg-gradient-to-br from-red-50 to-yellow-50 border-2 border-red-200 shadow-lg">
      <CardHeader className="text-center bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
        <CardTitle className="text-2xl font-bold flex items-center justify-center gap-4">
          <span>{sign.title}</span>
          <Badge className={`${getLevelColor(sign.level)} text-lg px-3 py-1`}>
            {sign.level}
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6">
        {/* 簽詩 */}
        <div className="text-center">
          <h3 className="text-xl font-semibold text-red-800 mb-3">簽詩</h3>
          <div className="bg-white p-6 rounded-lg border-2 border-red-200 shadow-inner temple-border">
            <pre className="text-lg leading-relaxed text-gray-800 fortune-poem whitespace-pre-wrap text-center">
              {sign.poem}
            </pre>
          </div>
        </div>

        {/* 簽意 */}
        <div>
          <h3 className="text-xl font-semibold text-red-800 mb-3">簽意解釋</h3>
          <div className="bg-white p-4 rounded-lg border-2 border-red-200 shadow-inner">
            <p className="text-gray-800 leading-relaxed">{sign.meaning}</p>
          </div>
        </div>

        {/* 各方面運程 */}
        <div>
          <h3 className="text-xl font-semibold text-red-800 mb-3">各方面運程</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(sign.aspects).map(([aspect, description]) => (
              <div key={aspect} className="bg-white p-4 rounded-lg border border-red-200 shadow-sm">
                <h4 className="font-semibold text-red-700 mb-2 text-center">{aspect}</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="flex justify-center gap-4 pt-4">
          <Button 
            onClick={handlePrint}
            variant="outline" 
            className="border-red-300 text-red-700 hover:bg-red-50"
          >
            <Download className="w-4 h-4 mr-2" />
            列印簽文
          </Button>
          <Button 
            onClick={handleShare}
            variant="outline" 
            className="border-red-300 text-red-700 hover:bg-red-50"
          >
            <Share2 className="w-4 h-4 mr-2" />
            分享簽文
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};